import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl, defineMessages } from 'react-intl';
import { Panel } from 'primereact/panel';
import { Button } from 'primereact/button';
import { useHistory, useParams } from 'react-router-dom';
import ArrayUtil from '../../../utils/ArrayUtil';
import { AccountContext } from '../../../components/AccountContext';

const messages = defineMessages({
  details: { id: 'superlink.details', defaultMessage: 'SuperLink Details' },
  manualActivation: {
    id: 'super_link.activation_type_manual',
    defaultMessage: 'Manual',
  },
  automaticActivation: {
    id: 'super_link.activation_type_automatic',
    defaultMessage: 'Automatic',
  },
  numberOfAssessmentsToDisableAfter: {
    id: 'super_link.number_of_assessments_to_disable_after',
    defaultMessage: 'Disable after assessments',
  },
  import: { id: 'res_import.file_field_label', defaultMessage: 'Import Respondents' },
});

const SuperLinkDetailPanel = ({ superLink, listReport }) => {
  const currentAccount = useContext(AccountContext);
  const history = useHistory();
  const { superLinkId } = useParams();
  const { formatMessage } = useIntl();

  const {
    activationOptions: { enabled, activationType, numberOfAssessmentsToDisableAfter },
  } = superLink;

  const getActivationTypeTranslation = () => {
    if (activationType === 'MANUAL') {
      return formatMessage(messages.manualActivation);
    }
    if (activationType === 'AUTOMATIC') {
      return formatMessage(messages.automaticActivation);
    }
    return '';
  };

  const getNumberOfAssessmentsToDisableAfterValueToDisplay = () => {
    if (numberOfAssessmentsToDisableAfter) return numberOfAssessmentsToDisableAfter;

    return '-';
  };

  const getNotificationEmails = () => {
    let notificationEmails = '';

    if (ArrayUtil.isNotEmpty(superLink.notificationEmails)) {
      superLink.notificationEmails.forEach((emailNotification, i) => {
        if (i !== 0) {
          notificationEmails += ', ';
        }
        notificationEmails = notificationEmails.concat(emailNotification);
      });
    }

    return notificationEmails;
  };

  const activationTypeTranslation = getActivationTypeTranslation();
  const numberOfAssessmentsToDisableAfterValue = getNumberOfAssessmentsToDisableAfterValueToDisplay();
  const notificationEmails = getNotificationEmails();

  return (
    <Panel header={formatMessage(messages.details)}>
      <div className="superLink-details">
        <span className="superLink-details-title">
          <FormattedMessage id="attrs.assessmentTemplate" defaultMessage="Assessment Template">
            {(text) => `${text}: `}
          </FormattedMessage>
        </span>
        <span>{superLink.assessmentTemplate.title}</span>
      </div>

      <div className="superLink-details">
        <span className="superLink-details-title">
          <FormattedMessage id="superlink.description" defaultMessage="Description">
            {(text) => `${text}: `}
          </FormattedMessage>
        </span>
        <span>{superLink.description}</span>
      </div>

      {superLink.targetJob ? (
        <div className="superLink-details">
          <span className="superLink-details-title">
            <FormattedMessage id="attrs.targetJob" defaultMessage="Target Job Profile">
              {(text) => `${text}: `}
            </FormattedMessage>
          </span>
          <span>{superLink.targetJob.name}</span>
        </div>
      ) : null}

      {superLink.reports ? (
        <div className="superLink-details">
          <span className="superLink-details-title">
            <FormattedMessage id="report.plural" defaultMessage="Reports">
              {(text) => `${text}: `}
            </FormattedMessage>
          </span>
          <span>{listReport}</span>
        </div>
      ) : null}

      {notificationEmails ? (
        <div className="superLink-details">
          <span className="superLink-details-title">
            <FormattedMessage id="superlink.notificationEmail" defaultMessage="Send auto-generated report(s) to">
              {(text) => `${text}: `}
            </FormattedMessage>
          </span>
          <span>{notificationEmails}</span>
        </div>
      ) : null}
      <div className="superLink-details">
        <span className="superLink-details-title">
          <FormattedMessage id="superlink.status" defaultMessage="SuperLink Status">
            {(text) => `${text}: `}
          </FormattedMessage>
        </span>
        {enabled ? (
          <span>
            <FormattedMessage id="superlink.enabled" defaultMessage="SuperLink enabled" />
          </span>
        ) : (
          <span>
            <FormattedMessage id="superlink.disabled" defaultMessage="SuperLink disabled" />
          </span>
        )}
      </div>
      <div className="superLink-details">
        <span className="superLink-details-title">
          <FormattedMessage id="superlink.activation_type" defaultMessage="SuperLink Activation Type">
            {(text) => `${text}: `}
          </FormattedMessage>
        </span>
        <span>{activationTypeTranslation}</span>
      </div>
      <div className="superLink-details">
        <span className="superLink-details-title">
          <FormattedMessage
            id="superlink.number_of_assessments_to_disable_after"
            defaultMessage="Disable after assessments"
          >
            {(text) => `${text}: `}
          </FormattedMessage>
        </span>
        <span>{numberOfAssessmentsToDisableAfterValue}</span>
      </div>
      <div className="superLink-details">
        <span className="superLink-details-title">
          <FormattedMessage
            id="superlink.send_individual_report_to_respondent"
            defaultMessage="Send Individual Report to respondent if distortion is acceptable"
          >
            {(text) => `${text}: `}
          </FormattedMessage>
        </span>
        {superLink.sendRespondentReport ? (
          <span>
            <FormattedMessage id="lbl_enabled" defaultMessage="Enabled" />
          </span>
        ) : (
          <span>
            <FormattedMessage id="lbl_disabled" defaultMessage="Disabled" />
          </span>
        )}
      </div>
      <div className="superLink-details">
        <span className="superLink-details-title">
          <FormattedMessage
            id="superlink.send_cc-interests_report_to_respondent"
            defaultMessage="Send Career Counselling - Interests Report to respondent"
          >
            {(text) => `${text}: `}
          </FormattedMessage>
        </span>
        {superLink.sendRespondentCcInterestsReport ? (
          <span>
            <FormattedMessage id="lbl_enabled" defaultMessage="Enabled" />
          </span>
        ) : (
          <span>
            <FormattedMessage id="lbl_disabled" defaultMessage="Disabled" />
          </span>
        )}
      </div>
      <div className="superLink-details">
        <span className="superLink-details-title">
          <FormattedMessage
            id="superlink.send_cc-personality_report_to_respondent"
            defaultMessage="Send Career Counselling - Personality Report to respondent"
          >
            {(text) => `${text}: `}
          </FormattedMessage>
        </span>
        {superLink.sendRespondentCcPersonalityReport ? (
          <span>
            <FormattedMessage id="lbl_enabled" defaultMessage="Enabled" />
          </span>
        ) : (
          <span>
            <FormattedMessage id="lbl_disabled" defaultMessage="Disabled" />
          </span>
        )}
      </div>
      <div className="superLink-details">
        <span className="superLink-details-title">
          <FormattedMessage id="superlink.lbl_printer_friendly_report" defaultMessage="Printer friendly report(s)">
            {(text) => `${text}: `}
          </FormattedMessage>
        </span>
        {superLink.printFriendlyReport ? (
          <span>
            <FormattedMessage id="lbl_enabled" defaultMessage="Enabled" />
          </span>
        ) : (
          <span>
            <FormattedMessage id="lbl_disabled" defaultMessage="Disabled" />
          </span>
        )}
      </div>
      <div className="superLink-details">
        <span className="superLink-details-title">
          <FormattedMessage id="attrs.notes" defaultMessage="Notes">
            {(text) => `${text}: `}
          </FormattedMessage>
        </span>
        {superLink.notes ? <span>{superLink.notes}</span> : null}
      </div>
      <Button
        type="button"
        className="btn btn-sm btn-primary"
        label={formatMessage(messages.import)}
        disabled={!enabled}
        onClick={() => {
          history.push(`/accounts/${currentAccount}/super-links/${superLinkId}/import`);
        }}
      />
    </Panel>
  );
};

SuperLinkDetailPanel.propTypes = {
  superLink: PropTypes.objectOf(PropTypes.any).isRequired,
  listReport: PropTypes.string.isRequired,
};

export default SuperLinkDetailPanel;
